
import React, { useState, useEffect } from 'react';
import { List, Plus, Loader2, MonitorPlay, Lock, Globe, Database, Gift, Play, Pencil, Trash2, FileSpreadsheet, BarChart2, Radio } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Survey } from '../types';
import { fetchSurveys, deleteSurvey } from '../services/surveyService';
import { useAutoRefresh } from '../hooks/useAutoRefresh';

// Helper for display text
const getLocalizedText = (obj: any) => obj?.en || Object.values(obj)[0] || 'Untitled';

const ResearcherView: React.FC = () => {
    const navigate = useNavigate();
    const [surveys, setSurveys] = useState<Survey[]>([]);
    const [loading, setLoading] = useState(false);

    const loadSurveys = async () => {
        setLoading(true);
        try {
            const data = await fetchSurveys();
            setSurveys(data);
        } catch (e: any) { 
            // Ignore AbortErrors that happen on rapid tab switching/reloading
            if (e.name !== 'AbortError' && e.message !== 'The user aborted a request.') {
                console.error(e); 
            }
        } finally { 
            setLoading(false); 
        }
    };

    useEffect(() => { loadSurveys(); }, []);

    // Auto-refresh survey list on tab focus
    useAutoRefresh(loadSurveys);

    const handleDelete = async (id: string) => {
        if (!window.confirm("Are you sure you want to delete this survey?")) return;
        try { 
            await deleteSurvey(id); 
            setSurveys(surveys.filter(s => s.id !== id)); 
        } catch (e) { 
            console.error("Failed to delete", e); 
            alert("Failed to delete survey"); 
        }
    };

    return (
        <div className="space-y-6 animate-in fade-in duration-300">
            {loading ? (
                <div className="flex justify-center py-12"><Loader2 className="animate-spin text-indigo-500" /></div>
            ) : surveys.length === 0 ? (
                <div className="text-center py-12 bg-slate-800/50 rounded-xl border border-slate-700 border-dashed">
                    <List size={48} className="mx-auto text-slate-600 mb-4" />
                    <h3 className="text-lg font-medium text-slate-300">No campaigns found</h3>
                    <p className="text-slate-500">Create your first survey to start gathering data.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {surveys.map(survey => (
                        <div key={survey.id} className="bg-slate-800 rounded-lg p-5 border border-slate-700 hover:border-indigo-500/50 transition-colors group flex flex-col h-full">
                            <div className="flex justify-between items-start mb-4">
                                <div className={`text-xs px-2 py-1 rounded font-mono flex items-center gap-2 ${survey.status === 'ACTIVE' ? 'bg-emerald-500/10 text-emerald-400' : survey.status === 'DRAFT' ? 'bg-slate-700 text-slate-300' : 'bg-yellow-500/10 text-yellow-400'}`}>
                                    {survey.status}
                                    {survey.require_login && <Lock size={10} className="text-orange-400 ml-1" />}
                                    {survey.is_kiosk && <span className="bg-purple-500/20 text-purple-300 px-1.5 rounded flex items-center gap-1"><MonitorPlay size={10}/> Kiosk</span>}
                                    {survey.is_live && <span className="bg-indigo-500/20 text-indigo-300 px-1.5 rounded flex items-center gap-1"><Radio size={10}/> Live</span>}
                                    {survey.source === 'IMPORT_CSV' && <span className="bg-sky-500/20 text-sky-300 px-1.5 rounded flex items-center gap-1" title="Imported external data — analysis only, no rewards"><FileSpreadsheet size={10}/> Imported</span>}
                                </div>
                                <div className="text-slate-400 text-xs">{new Date(survey.created_at).toLocaleDateString()}</div>
                            </div>
                            
                            <div className="flex-1">
                                <h3 className="font-bold text-white text-lg mb-1 truncate" title={getLocalizedText(survey.title)}>
                                    {getLocalizedText(survey.title)}
                                </h3>
                                <div className="flex gap-2 items-center text-xs mt-2">
                                    {survey.require_login ? (
                                        <span className="text-orange-400 flex items-center gap-1"><Lock size={12}/> Member Only</span>
                                    ) : (
                                        <span className="text-blue-400 flex items-center gap-1"><Globe size={12}/> Public Access</span>
                                    )}
                                </div>
                            </div>

                            <div className="mt-4 pt-4 border-t border-slate-700 flex items-center justify-between">
                                <div className="flex items-center gap-3 text-sm text-slate-400">
                                    {survey.reward_type === 'DIGITAL' ? (
                                        <span className="flex items-center gap-1 text-emerald-400 font-medium"><Database size={14}/> {survey.digital_reward?.amount || 0} {survey.digital_reward?.currency || 'USDT'}</span>
                                    ) : survey.reward_type === 'PHYSICAL' ? (
                                        <span className="flex items-center gap-1 text-indigo-400 font-medium"><Gift size={14}/> {survey.loot_table?.name || 'Loot Box'}</span>
                                    ) : (
                                        <span className="text-slate-500">No Reward</span>
                                    )}
                                </div>
                                <div className="flex items-center gap-2">
                                    <button onClick={() => navigate(`/survey/${survey.id}`)} title="Preview Survey" className="p-2 text-slate-400 hover:text-indigo-400 hover:bg-indigo-900/20 rounded-lg transition-colors"><Play size={16} /></button>
                                    {survey.is_live && <button onClick={() => navigate(`/research/campaigns/${survey.id}/present`)} title="Present Live" className="p-2 text-slate-400 hover:text-indigo-400 hover:bg-indigo-900/20 rounded-lg transition-colors"><Radio size={16} /></button>}
                                    <button onClick={() => navigate(`/research/campaigns/${survey.id}/dashboard`)} title="Dashboard & Analysis" className="p-2 text-slate-400 hover:text-indigo-400 hover:bg-indigo-900/20 rounded-lg transition-colors"><BarChart2 size={16} /></button>
                                    <button onClick={() => navigate(`/research/campaigns/${survey.id}/responses`)} title="View Responses" className="p-2 text-slate-400 hover:text-emerald-400 hover:bg-emerald-900/20 rounded-lg transition-colors"><List size={16} /></button>
                                    <button onClick={() => navigate(`/research/create?edit=${survey.id}`)} className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors" title="Edit Survey"><Pencil size={16} /></button>
                                    <button onClick={() => handleDelete(survey.id)} className="p-2 text-slate-400 hover:text-red-400 hover:bg-red-900/20 rounded-lg transition-colors"><Trash2 size={16} /></button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default ResearcherView;
